import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { timeAgo } from '@/lib/format';
import { useMarkRead, useNotifications, useUnreadCount } from '@shared/hooks/use-notifications';
import { BellIcon } from './icons';

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: unread = 0 } = useUnreadCount();
  const { data: page, isLoading } = useNotifications(open);
  const markRead = useMarkRead();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const items = page?.items ?? [];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-full p-2 text-gray-600 hover:bg-gray-100"
        aria-label="Notifications"
      >
        <BellIcon />
        {unread > 0 ? (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-700 px-1 text-[10px] font-semibold text-white',
            )}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="fixed inset-x-3 top-14 z-20 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[380px]">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <button
              className="text-xs text-brand-700 hover:underline disabled:opacity-50"
              onClick={() => markRead.mutate({ markAll: true })}
              disabled={unread === 0 || markRead.isPending}
            >
              Mark all as read
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <p className="p-6 text-center text-sm text-gray-500">Loading…</p>
            ) : items.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-500">You&rsquo;re all caught up.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((n) => (
                  <li
                    key={n.id}
                    className={cn(
                      'flex items-start gap-3 px-4 py-3',
                      !n.readAt && 'bg-brand-50/40',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1 h-2 w-2 shrink-0 rounded-full',
                        n.readAt ? 'bg-transparent' : 'bg-brand-700',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-xs uppercase tracking-wide text-gray-500">
                          {n.type.replace(/_/g, ' ').toLowerCase()}
                        </span>
                        <span className="shrink-0 text-[11px] text-gray-400">
                          {timeAgo(n.createdAt)}
                        </span>
                      </div>
                      <h4 className="mt-0.5 text-sm font-medium text-gray-900">{n.title}</h4>
                      {n.body ? <p className="mt-0.5 text-sm text-gray-600">{n.body}</p> : null}
                      <div className="mt-2 flex items-center gap-3">
                        {n.link ? (
                          <a
                            href={n.link}
                            className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-800"
                          >
                            View
                          </a>
                        ) : null}
                        {!n.readAt ? (
                          <button
                            className="text-xs text-gray-500 hover:underline"
                            onClick={() => markRead.mutate({ ids: [n.id] })}
                          >
                            Mark as read
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
